import { mstimezones } from "@assets/timezones";
import { Settings, DateTime } from "luxon";
import { TFunction } from "react-i18next";

export const convertToUserTimeZone = (userTimeZone?: string) => {
  return mstimezones.find((zones) => zones.name === userTimeZone)?.momenttz;
};

export const setDefaultTimeZone = (userTimeZone: string) => {
  // Configure the time zone
  Settings.defaultZone = convertToUserTimeZone(userTimeZone) ?? "utc";
};
export const convertTimeToTimeZone = (
  dateTime: Date,
  dateFormatDescription: Intl.DateTimeFormatOptions = {},
  userTimeZone?: string
) =>
  dateTime.toLocaleString("en-US", {
    ...dateFormatDescription,
    timeZone: convertToUserTimeZone(userTimeZone),
  });

export const convertLocalTime = (dateTime: Date, tz?: string) => {
  let ti = DateTime.fromJSDate(
    dateTime,
    tz ? { zone: convertToUserTimeZone(tz) } : {}
  );
  return ti.toJSDate();
};

export const toStartOfDay = (date: Date): Date => {
  let ti = DateTime.fromJSDate(date).set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 1,
  });
  return ti.toJSDate();
};

export const toEndOfDay = (date: Date): Date => {
  let ti = DateTime.fromJSDate(date).set({
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  });
  return ti.toJSDate();
};

export const toHHMMSS = (duration: number): string[] => {
  duration /= 1000;
  var hours = Math.floor(duration / 3600);
  var minutes = Math.floor((duration - hours * 3600) / 60);
  var seconds = Math.floor(duration - hours * 3600 - minutes * 60);

  return [
    hours < 10 ? "0" + hours : hours.toString(),
    minutes < 10 ? "0" + minutes : minutes.toString(),
    seconds < 10 ? "0" + seconds : seconds.toString(),
  ];
};

export const sameDay = (first: Date, second: Date) =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

export const getDefaultDates = () => {
  const now = new Date();
  now.setMonth(now.getMonth() - 3);
  let start = toStartOfDay(now);
  now.setMonth(now.getMonth() + 4);
  let end = toEndOfDay(now);
  return { start, end };
};

export const fromNow = (
  date: number,
  bPrecise: boolean = false,
  completeTimeUnit: boolean = false,
  unformatted: boolean = false
): [template: string, params?: {}] => {
  const now = new Date().getTime();
  const diff = now - date;
  const sign = Math.sign(diff);
  const secondsInMs = 1e3;
  const minutesInMs = 6e4;
  const hoursInMs = minutesInMs * 60;
  const daysInMs = hoursInMs * 24;

  const flooredDiff = (scale: number) => Math.floor(Math.abs(diff) / scale);
  const { seconds, minutes, hours, days } = {
    seconds: {
      value: flooredDiff(secondsInMs) % 60,
      symbol: "s",
      unit: "second",
    },
    minutes: {
      value: flooredDiff(minutesInMs) % 60,
      symbol: "m",
      unit: "minute",
    },
    hours: {
      value: flooredDiff(hoursInMs) % 24,
      symbol: "h",
      unit: "hour",
    },
    days: { value: flooredDiff(daysInMs), symbol: "d", unit: "day" },
  };

  let selected = [];
  if (days.value > 99)
    return [
      `${sign < 0 && !unformatted ? "in " : ""}99+ days${
        sign > 0 && !unformatted ? " ago" : ""
      }`,
    ];
  else if (days.value > 0) selected = bPrecise ? [days, hours] : [days];
  else if (hours.value > 0) selected = bPrecise ? [hours, minutes] : [hours];
  else if (minutes.value > 0) selected = [minutes];
  else if (seconds.value > 0)
    selected = [{ value: 1, symbol: "m", unit: "minute" }];
  else return ["Now"];
  return [
    `${sign < 0 && !unformatted ? "in " : ""}${selected
      .map(({ symbol, unit, value }) =>
        completeTimeUnit
          ? value > 1
            ? `{{value${unit}}} ${unit}s`
            : `{{value${unit}}} ${unit}`
          : `{{value${symbol}}}${symbol}`
      )
      .join(" ")}${sign > 0 ? " ago" : ""}`,
    selected.reduce(
      (acc, { symbol, value, unit }) => ({
        ...acc,
        [`value${completeTimeUnit ? unit : symbol}`]: value,
      }),
      {}
    ),
  ];
};

export const sinceTime = (time: number, t: TFunction) => {
  time = Math.abs(time);
  if (time) {
    var weeks = Math.floor(time / (7 * 24 * 3600000));
    if (weeks > 4) {
      time = new Date().getTime() - time;
      return t("Since {{ date }}", {
        date: DateTime.fromMillis(time).toLocaleString(DateTime.DATETIME_MED),
      });
    } else {
      if (weeks > 0) {
        time = time - weeks * (7 * 24 * 3600000);
        var days = Math.floor(time / (24 * 3600000));
        time = time - days * (24 * 3600000);
        var hours: number | string = Math.floor(time / 3600000);
        time = time - hours * 3600000;
        if (hours < 10) hours = "0" + hours;
        var minutes: number | string = Math.floor(time / 60000);
        if (minutes < 10) minutes = "0" + minutes;
        return t(
          "{{ nbWeeks }} {{ weeks }} {{ nbDays }} {{ days }} {{ nbHours }}:{{ nbMinutes }}",
          {
            nbWeeks: weeks,
            nbDays: days,
            nbHours: hours,
            nbMinutes: minutes,
            weeks: weeks > 1 ? t("weeks") : t("week"),
            days: days > 1 ? t("days") : t("day"),
          }
        );
      } else {
        var days = Math.floor(time / (24 * 3600000));

        if (days > 0) {
          time = time - days * (24 * 3600000);
          var hours: number | string = Math.floor(time / 3600000);
          time = time - hours * 3600000;
          if (hours < 10) hours = "0" + hours;
          var minutes: number | string = Math.floor(time / 60000);
          if (minutes < 10) minutes = "0" + minutes;
          return t("{{ nbDays }} {{ days }} {{ nbHours }}:{{ nbMinutes }}", {
            nbDays: days,
            nbHours: hours,
            nbMinutes: minutes,
            days: days > 1 ? t("days") : t("day"),
          });
        } else {
          var hours: number | string = Math.floor(time / 3600000);
          time = time - hours * 3600000;
          var minutes: number | string = Math.floor(time / 60000);

          var timedisplay = "{{ nbHours }} hours {{ nbMinutes }} minutes";
          if (hours < 1) {
            if (minutes < 1) {
              timedisplay = "less than a minute";
            } else if (minutes == 1) {
              timedisplay = "{{ nbMinutes }} minute";
            } else {
              timedisplay = "{{ nbMinutes }} minutes";
            }
          } else if (hours == 1) {
            if (minutes < 1) {
              timedisplay = "{{ nbHours }} hour";
            } else if (minutes == 1) {
              timedisplay = "{{ nbHours }} hour {{ nbMinutes }} minute";
            }
          } else {
            if (minutes < 1) {
              timedisplay = "{{ nbHours }} hours";
            } else if (minutes == 1) {
              timedisplay = "{{ nbHours }} hours {{ nbMinutes }} minute";
            }
          }

          if (hours < 10) hours = "0" + hours;

          if (minutes < 10) minutes = "0" + minutes;

          return t(timedisplay, { nbHours: hours, nbMinutes: minutes });
        }
      }
    }
  }
  return "-";
};

const addOrdinal = (number: number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = number % 100;
  return number + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

export const displayMonthDay = (time?: number | null, tz?: string) => {
  if (!time) return "-";
  let ti = DateTime.fromMillis(time, tz ? { zone: tz } : {});
  return `${ti.toFormat("MMMM").charAt(0).toUpperCase()}${ti
    .toFormat("MMMM")
    .slice(1)} ${addOrdinal(ti.day)}`;
};
export const displayMonth = (time?: number | null, tz?: string) => {
  if (!time) return "-";
  let ti = DateTime.fromMillis(time, tz ? { zone: tz } : {});
  return `${ti.toFormat("MMMM").charAt(0).toUpperCase()}${ti
    .toFormat("MMMM")
    .slice(1)}`;
};

export const displayDate = (time?: number | null, tz?: string) => {
  if (!time) return "-";
  let ti = DateTime.fromMillis(time, tz ? { zone: tz } : {});
  return ti.toFormat("ddLLLyyyy").toUpperCase();
};
export const displayDateFromIso = (
  time?: string | null,
  userTimeZone?: string
) => {
  if (!time) return "-";
  let tiUTC = DateTime.fromISO(time, { zone: "Etc/UTC" });
  let tz = convertToUserTimeZone(userTimeZone) ?? "utc";
  let ti = DateTime.fromMillis(tiUTC.toMillis(), tz ? { zone: tz } : {});
  return ti.toFormat("ddLLLyyyy").toUpperCase();
};

export const displayTime = (time?: number | null, tz?: string) => {
  if (!time) return "-";
  let ti = DateTime.fromMillis(time, tz ? { zone: tz } : {});
  return ti.toFormat("hh:mm a");
};

export const displayDateTime = (time?: number | null, tz?: string) => {
  if (!time) return "-";
  let ti = DateTime.fromMillis(time, tz ? { zone: tz } : {});
  return `${ti.toFormat("ddLLLyyyy").toUpperCase()} ${ti.toFormat("hh:mm a")} `;
};

export const displayDateCSV = (time?: number | null, tz?: string) => {
  if (!time) return "";
  let ti = DateTime.fromMillis(time, tz ? { zone: tz } : {});
  return ti.toFormat("yyyy-MM-dd");
};

export const displayDateTimeCSV = (time?: number | null, tz?: string) => {
  if (!time) return "";
  let ti = DateTime.fromMillis(time, tz ? { zone: tz } : {});
  return ti.toFormat("yyyy-MM-dd HH:mm:ss");
};

export const getResumeMonitoringTime = (
  date?: number,
  tooltip?: boolean
): string => {
  const now = new Date().getTime();
  const diff = (date ?? 0) - now;
  const minutesInMs = 6e4;
  const hoursInMs = minutesInMs * 60;
  const daysInMs = hoursInMs * 24;

  const flooredDiff = (scale: number) => Math.floor(diff / scale);
  const days = { value: flooredDiff(daysInMs), symbol: "d", unit: "day" };
  const minutes = {
    value: flooredDiff(minutesInMs) % 60,
    symbol: "m",
    unit: "minute",
  };

  if (days.value > 99) return `99+ Days ${tooltip ? "Remaining" : ""}`;
  else if (days.value === 0 && minutes.value > 0)
    return `<1 Day ${tooltip ? "Remaining" : ""}`;
  else if (days.value > 0)
    return `${days.value} ${days.value === 1 ? "Day" : "Days"} ${
      tooltip ? "Remaining" : ""
    }`;
  else return tooltip ? "Indefinitely" : "∞";
};

// https://github.com/mui/mui-x/blob/86ac3d2b90da1f889fe47d5f7a7930fc97b8ed23/packages/x-date-pickers/src/LocalizationProvider/LocalizationProvider.tsx
// MUI default value
export const getDefaultMinMaxDates = {
  minDate: new Date("1900-01-01T00:00:00.000")!,
  maxDate: new Date("2099-12-31T00:00:00.000")!,
};
